import { Box, Flex } from '@chakra-ui/layout';
import { Button, Checkbox, Skeleton } from '@chakra-ui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import React, {
  FormEventHandler,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useVisibleOrganizationsQuery } from '../implementations.gql.generated';
import { SearchBarInput } from 'packages/common-frontend/components/SearchAndCopyComponents';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import {
  Org,
  WizardComponent,
} from '~/core/routes/settings/implementations/create/ImplementationWizard';

const CheckBoxList = ({
  list,
  defaultChecked,
  isLoading,
}: {
  list: Org[];
  defaultChecked?: boolean;
  isLoading?: boolean;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Here we must use tanstack-virtual because the list of organizations could have
  // 500 to 1000 items. I tried rendering a list of this size without the virtualizer
  // and the performance was unacceptable.
  const virtualizer = useVirtualizer({
    count: list.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 45,
    getItemKey: (index) => list[index].name,
  });

  if (isLoading) {
    return <Skeleton h="400px" mt="0.4em" data-testid="skeleton" />;
  }

  return (
    <Box
      ref={scrollRef}
      height="25rem"
      overflowY="auto"
      mt="0.5rem"
      data-testid="virtualized-list"
    >
      <Box height={`${virtualizer.getTotalSize()}px`} position="relative">
        {virtualizer.getVirtualItems().map((virtualItem) => (
          <div
            key={virtualItem.key}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualItem.start}px)`,
            }}
            data-index={virtualItem.index}
            ref={virtualizer.measureElement}
          >
            <Box
              borderWidth="thin"
              px="1em"
              py="0.6rem"
              my="0.4rem"
              borderRadius="8px"
            >
              <Checkbox
                value={list[virtualItem.index].id}
                defaultChecked={defaultChecked}
              >
                {list[virtualItem.index].name}
              </Checkbox>
            </Box>
          </div>
        ))}
      </Box>
    </Box>
  );
};

export const ChooseOrganizations: WizardComponent = ({
  selectedOrgs,
  setSelectedOrgs,
  nextStep,
}) => {
  const { user } = useUserAuthOrThrow();
  const { data, loading, error } = useVisibleOrganizationsQuery({
    variables: { organizationId: user.organizationId },
  });

  if (error) throw error;

  const orgs = useMemo(() => data?.organization.visibleOrganizations || [], [
    data,
  ]);

  const [searchQuery, setSearchQuery] = useState('');

  const onFormChange: FormEventHandler<HTMLFormElement> = useCallback(
    (event) => {
      const target = event.target;

      if (target instanceof HTMLInputElement) {
        const orgId = target.value;
        const selectedOrg = orgs.find((org) => org.id === orgId);

        if (target.checked && selectedOrg) {
          setSelectedOrgs((prevSelectedOrgs) => [
            ...prevSelectedOrgs,
            selectedOrg,
          ]);
        } else {
          setSelectedOrgs((prevSelectedOrgs) =>
            prevSelectedOrgs.filter((prevOrg) => prevOrg.id !== orgId),
          );
        }
      }
    },
    [orgs, setSelectedOrgs],
  );

  const filteredData = useMemo(
    () =>
      orgs.filter(
        (org) =>
          org.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !selectedOrgs.find((selectedOrg) => selectedOrg.id === org.id),
      ),
    [selectedOrgs, searchQuery, orgs],
  );

  return (
    <Box width="100%">
      <Box>
        <Typography variant="page-title">Set up your Facility</Typography>
        <Typography variant="base-muted">
          Your implementation will start with a structured approach to establish
          your initial facility. Start by adding organizations to your facility.
        </Typography>
        <Flex
          gap="24px"
          mt="1rem"
          my="2rem"
          data-testid="unselected-organizations"
        >
          <Box width="50%" borderWidth="thin" borderRadius="8px" padding="2rem">
            <Box mb="0.5rem">
              <Typography variant="base" fontWeight="bold">
                Select Organizations
              </Typography>
            </Box>
            <SearchBarInput
              query={searchQuery}
              onChangeQuery={setSearchQuery}
            />
            <form onChange={onFormChange}>
              <CheckBoxList list={filteredData} isLoading={loading} />
            </form>
          </Box>
          <Box
            width="50%"
            borderWidth="thin"
            borderRadius="8px"
            padding="2rem"
            data-testid="selected-organizations"
          >
            <Typography variant="base" fontWeight="bold">
              Selected
            </Typography>
            <form onChange={onFormChange}>
              <CheckBoxList list={selectedOrgs} defaultChecked />
            </form>
          </Box>
        </Flex>
      </Box>
      <Flex justify="end">
        <Button
          type="button"
          variant="primary"
          colorScheme="v2-aurora"
          onClick={nextStep}
          isDisabled={selectedOrgs.length === 0}
        >
          Next
        </Button>
      </Flex>
    </Box>
  );
};
